'use client'

import { FC } from 'react'

import Link from 'next/link'

import GenericCloneComponent from '../GenericCloneComponent/GenericCloneComponent'
import Loading from '../Loading/Loading'
import { ButtonProps } from './types'

const Button: FC<ButtonProps> = ({
  className,
  text,
  buttonSize = 'normal',
  textSize = 'normal',
  variant = 'primary',
  textAlign = 'center',
  iconLeft,
  classNameIconLeft,
  iconRight,
  classNameIconRight,
  href,
  isLoading,
  allowClickOnLoading = false,
  onClick,
  ...props
}) => {
  const buttonSizeCase = {
    small: 'h-[42px]',
    normal: 'h-[50px]',
    large: 'h-[58px]',
  }

  const textSizeCase = {
    small: 'prose-button-small',
    normal: 'prose-button',
    large: 'prose-button-large',
  }

  const textAlignCase = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  }

  const buttonStyleDefault = `flex max-h-[50px] gap-2 items-center justify-center px-4 rounded-lg disabled:text-primary-900 cursor-pointer disabled:bg-grayscale-200 disabled:cursor-not-allowed active:scale-95 transition duration-75
    ${(iconLeft && !isLoading) || (iconRight && !isLoading) ? 'py-3' : isLoading ? 'py-[13px]' : 'py-3.5'} ${isLoading ? 'pointer-events-none' : 'pointer-events-auto'}`

  const buttonStyleCase = {
    primary:
      'border-[1px] bg-primary-500 text-white border-primary-500 disabled:border-grayscale-200 hover:bg-primary-300 hover:text-neutral-white hover:border-primary-300 active:bg-primary-500 active:border-primary-500',
    secondary:
      'border-[1px] bg-withe text-primary-600 border-primary-300 hover:text-primary-500',
    tertiary:
      'bg-withe text-primary-600 hover:text-primary-500 active:primary-300',
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isLoading && !allowClickOnLoading) {
      event.preventDefault()
      return
    }

    if (onClick) {
      onClick(event)
    }
  }

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {href ? (
        <Link href={href}>
          <button
            className={`
              ${buttonStyleDefault}
              ${buttonStyleCase[variant]}
              ${textSizeCase[textSize]}
              ${textAlignCase[textAlign]}
              ${buttonSizeCase[buttonSize]}
              ${className}
            `}
            onClick={handleClick}
            {...props}
          >
            {iconLeft ? (
              <GenericCloneComponent
                propsMapper={() => ({ className: classNameIconLeft })}
              >
                {iconLeft}
              </GenericCloneComponent>
            ) : null}
            {text}
            {isLoading ? <Loading /> : null}
            {iconRight ? (
              <GenericCloneComponent
                propsMapper={() => ({ className: classNameIconRight })}
              >
                {iconRight}
              </GenericCloneComponent>
            ) : null}
          </button>
        </Link>
      ) : (
        <button
          className={`
            ${buttonStyleDefault}
            ${buttonStyleCase[variant]}
            ${textSizeCase[textSize]}
            ${textAlignCase[textAlign]}
            ${buttonSizeCase[buttonSize]}
            ${className}
          `}
          onClick={handleClick}
          {...props}
        >
          {iconLeft && !isLoading ? (
            <GenericCloneComponent
              propsMapper={() => ({ className: classNameIconLeft })}
            >
              {iconLeft}
            </GenericCloneComponent>
          ) : null}
          {!isLoading ? text : null}
          {isLoading ? <Loading /> : null}
          {iconRight && !isLoading ? (
            <GenericCloneComponent
              propsMapper={() => ({ className: classNameIconRight })}
            >
              {iconRight}
            </GenericCloneComponent>
          ) : null}
        </button>
      )}
    </>
  )
}

export default Button
